import { AfterViewChecked, AfterViewInit, Component, ElementRef, EventEmitter, Input, OnDestroy, Output, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { FormatApproximateCount } from '../../finder/result-count';
import { InkStyle, InkTabBox, MeasureInk } from './tab-strip.ink';

/** One tab of an {@link AGUTabStripComponent}. `Count` rides with the tab because the strip's badge is never independent of it. */
export interface AGUTabStripTab {
    readonly Key: string;
    readonly Label: string;
    /** Badge number, e.g. how many results the tab holds. Absent or null renders no badge — 0 is a real, renderable count. */
    readonly Count?: number | null;
    /** True when `Count` is an estimate (a listing the engine read only part of the way) — the badge reads "about 15,199", like the toolbar caption's `FormatCountLine`. */
    readonly CountApproximate?: boolean;
}

/**
 * The portal's view tabs — the mockup's `.viewtabs`/`.vtab`: a flat underlined
 * strip with a count pill per tab, re-inked in the module accent when active,
 * and the mockup's `.tab` pill row (`Variant="pill"`) for the two screens that
 * draw their tabs that way.
 *
 * It replaces `mj-tab-nav` on the finders, whose pill-shaped tabs and badge
 * palette are a different design language from either of the mockup's two.
 */
@Component({
    standalone: false,
    selector: 'agu-tab-strip',
    templateUrl: './tab-strip.component.html',
    styleUrl: './tab-strip.component.scss',
})
/** The tab strip the finders and detail views share: underline or pill tabs with optional count badges, a tablist that points at one results panel, and an `ActiveChange` event. */
export class AGUTabStripComponent implements AfterViewInit, AfterViewChecked, OnDestroy {
    @Input() Tabs: readonly AGUTabStripTab[] = [];
    /** Key of the tab currently shown. */
    @Input() Active = '';
    /**
     * `id` of the region the tabs switch — the results panel below them. Every
     * tab points at the same element because there IS one panel whose content
     * changes, not one panel per tab; without it a screen reader announces
     * "tab, selected" with nowhere to move to. Empty renders no aria-controls.
     */
    @Input() PanelId: string | null = null;
    /**
     * Which tab language to draw. The finders and the detail views underline
     * (`.vtab`, grants.html); the Saved board and Search everything use the
     * mockup's pill (`saved.html:261-262`, `search.html:263-264`). Both draw
     * the SAME tablist — only the paint differs, so a screen picking a variant
     * never gives up the count badge or the aria contract.
     */
    @Input() Variant: 'underline' | 'pill' = 'underline';
    /**
     * On a phone, a pill row that becomes one segmented control with a column per tab — the Saved board's `.filterbar .tabs`
     * (P/Member_Hub.html:752-754). Other pill rows scroll on one line instead (:744-746); the underline variant is always segmented
     * on a phone (:656-661).
     */
    @Input() PhoneSegmented = false;

    @Output() ActiveChange = new EventEmitter<string>();

    /** The tab buttons, measured to place the ink. */
    @ViewChildren('tabButton') private TabButtons?: QueryList<ElementRef<HTMLElement>>;
    @ViewChild('ink') private InkElement?: ElementRef<HTMLElement>;

    /** The ink bar's current style; collapsed until the first measure. */
    InkStyle: InkStyle = { width: '0px', transform: 'translateX(0px)', transition: 'none' };

    private Painted = false;
    /** What the tabs showed at the last measure (`ShownKey`); a view check measures again only when it differs. */
    private MeasuredKey = '';
    private Destroyed = false;
    private SizeObserver: ResizeObserver | null = null;
    private readonly OnResize = (): void => this.Remeasure(true);

    ngAfterViewInit(): void {
        this.MeasuredKey = this.ShownKey();
        this.Remeasure(true);
        window.addEventListener('resize', this.OnResize);
        // The strip's own size changing (a kept-mounted module shown again after display:none, a container resize) re-measures too.
        const list = this.InkElement?.nativeElement.parentElement;
        if (list && typeof ResizeObserver === 'function') {
            this.SizeObserver = new ResizeObserver(() => { if (!this.Destroyed) this.Remeasure(true); });
            this.SizeObserver.observe(list);
        }
        // Web fonts change tab widths after first layout (P/grant-finder.html:1205-1215); never after destroy (L46).
        void document.fonts?.ready.then(() => { if (!this.Destroyed) this.Remeasure(true); });
    }

    /**
     * Measures only when what the tabs show changed since the last pass (L46): reading tab offsets forces a layout, and view checks
     * run on every change detection of the page around the strip. The finder pages rebuild `Tabs` on every pass, so the comparison
     * is by content (`ShownKey`), not by reference.
     */
    ngAfterViewChecked(): void {
        const key = this.ShownKey();
        if (key === this.MeasuredKey) return;
        this.MeasuredKey = key;
        this.Remeasure(false);
    }

    /** Everything that moves the ink: the variant, the active tab, and each tab's label and badge text. No layout read. */
    private ShownKey(): string {
        const tabs = this.Tabs.map((t) => `${t.Key}\u0001${t.Label}\u0001${this.HasCount(t) ? this.CountLabel(t) : ''}`).join('\u0002');
        return `${this.Variant}|${this.PhoneSegmented}|${this.Active}|${tabs}`;
    }

    ngOnDestroy(): void {
        this.Destroyed = true;
        window.removeEventListener('resize', this.OnResize);
        this.SizeObserver?.disconnect();
        this.SizeObserver = null;
    }

    /**
     * Places the ink under the active tab. Zoneless Explorer: the style is
     * written straight to the element, so no change-detection pass is needed.
     */
    private Remeasure(snap: boolean): void {
        const buttons = this.TabButtons?.map((ref) => ref.nativeElement) ?? [];
        this.MeasureFrom(buttons, snap);
    }

    /** Computes {@link InkStyle} from tab boxes and applies it when it changed. `snap` forces no transition (first paint, resize, fonts). */
    MeasureFrom(tabs: ReadonlyArray<InkTabBox>, snap: boolean): void {
        const index = this.Tabs.findIndex((tab) => tab.Key === this.Active);
        const reduced = typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;
        const next = MeasureInk(tabs, index, snap || !this.Painted, reduced);
        const prev = this.InkStyle;
        if (next.width === prev.width && next.transform === prev.transform) {
            return;
        }
        this.InkStyle = next;
        if (tabs.length > 0) {
            this.Painted = true;
        }
        const el = this.InkElement?.nativeElement;
        if (el) {
            el.style.width = next.width;
            el.style.transform = next.transform;
            el.style.transition = next.transition;
        }
    }

    IsActive(tab: AGUTabStripTab): boolean {
        return tab.Key === this.Active;
    }

    /** 0 is a count worth showing ("All grants 0"); absent is not. */
    HasCount(tab: AGUTabStripTab): boolean {
        return tab.Count !== undefined && tab.Count !== null;
    }

    /** The badge text: the grouped number ("15,199"), or "about 15,199" (FormatApproximateCount) for an approximate count. */
    CountLabel(tab: AGUTabStripTab): string {
        const count = tab.Count ?? 0;
        return tab.CountApproximate === true ? FormatApproximateCount(count) : count.toLocaleString('en-US');
    }

    /** Re-clicking the active tab is not a change, and finder pages re-run a search on every change. */
    Select(key: string): void {
        if (key === this.Active) {
            return;
        }
        this.ActiveChange.emit(key);
    }
}
